import React, { useState } from 'react';
import {
    Menu, Bell, LogOut, User as UserIcon, Settings as SettingsIcon,
    ChevronDown, Hand, CheckCircle2, AlertTriangle, CalendarPlus, UserRoundPlus, CreditCard, Clock,
    PanelLeftClose, PanelLeftOpen, Eye, Undo2
} from 'lucide-react';
// IMPORTA useNavigate
import { useNavigate } from 'react-router-dom';
import { Page } from '../../types';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useUsers } from '@/contexts/UsersContext';
import { getInitials } from '@/lib/avatarUtils';
import { useNotifications } from '@/contexts/NotificationsContext';
import NotificationsModal from '../modals/NotificationsModal';

interface TopBarProps {
    onToggleSidebar: () => void;
    onNavigate: (page: Page) => void;
    isSidebarCollapsed?: boolean;
    toggleSidebarCollapse?: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onToggleSidebar, onNavigate, isSidebarCollapsed = false, toggleSidebarCollapse }) => {
    const { t } = useTranslation();
    const { logout, user, realUser, viewAsUser, startViewAs, stopViewAs, permissionLevel, organization } = useAuth();
    const { users } = useUsers();
    const { notifications, markAsRead, markAllAsRead } = useNotifications();

    // Inizializza il navigate
    const navigate = useNavigate();

    // Stati
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [isFullNotificationsModalOpen, setIsFullNotificationsModalOpen] = useState(false);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    const getNotifIcon = (type: string) => {
        switch (type) {
            case 'free_trial': return <Clock size={16} className="text-blue-500" />;
            case 'booking': return <CalendarPlus size={16} className="text-purple-500" />;
            case 'client': return <UserRoundPlus size={16} className="text-green-500" />;
            case 'billing': return <CreditCard size={16} className="text-red-500" />;
            case 'success': return <CheckCircle2 size={16} className="text-green-500" />;
            case 'alert': return <AlertTriangle size={16} className="text-red-500" />;
            default: return <Bell size={16} className="text-gray-500" />;
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return 'Adesso';
        if (diffMin < 60) return `${diffMin}m`;
        const diffHour = Math.floor(diffMin / 60);
        if (diffHour < 24) return `${diffHour}h`;
        return `${Math.floor(diffHour / 24)}g`;
    };

    const unreadCount = notifications.filter((n: any) => !n.read).length;
    const firstName = (user as any)?.first_name ?? (user as any)?.user_metadata?.first_name ?? '';
    const lastName = (user as any)?.last_name ?? (user as any)?.user_metadata?.last_name ?? '';
    const fullName = `${firstName} ${lastName}`.trim() || (user as any)?.email || 'Utente';
    const displayEmail = (user as any)?.email ?? '';

    // Identità dell'account attivo: ruolo esplicito in italiano + salone, per chiarire
    // sempre CHI sta operando (le azioni vengono attribuite a questo account).
    const roleLabel = permissionLevel === 'owner'
        ? t('topbar.role.owner', 'Titolare')
        : permissionLevel === 'senior'
            ? t('topbar.role.senior', 'Senior')
            : t('topbar.role.junior', 'Junior');
    const isRealAdmin = (realUser as any)?.role === 'admin';
    const realFirstName = (realUser as any)?.first_name ?? '';
    // "Vedi come": dipendenti attivi diversi dall'account loggato
    const viewAsCandidates = (users || []).filter((u: any) => u.is_active !== false && u.id !== (realUser as any)?.id);

    return (
        <>
            <header className={`h-20 bg-white border-b border-gray-100 flex items-center justify-between px-6 fixed top-0 right-0 z-[60] shadow-sm/30 transition-all duration-300 ${isSidebarCollapsed ? 'left-0 lg:left-20' : 'left-0 lg:left-48 xl:left-60'}`}>
                {/* SINISTRA */}
                <div className="flex items-center gap-4">
                    {/* Mobile Menu Button */}
                    <button onClick={onToggleSidebar} className="p-2 -ml-2 rounded-lg hover:bg-gray-100 lg:hidden text-gray-600 transition-colors">
                        <Menu size={20} />
                    </button>

                    {/* Desktop Toggle Button */}
                    {toggleSidebarCollapse && (
                        <button
                            onClick={toggleSidebarCollapse}
                            className="hidden lg:flex p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors mr-2"
                            title={isSidebarCollapsed ? "Espandi Sidebar" : "Riduci Sidebar"}
                        >
                            {isSidebarCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
                        </button>
                    )}

                    <div className="relative">
                        <button
                            onClick={() => { setIsProfileOpen(!isProfileOpen); setIsNotificationsOpen(false); }}
                            className={`flex items-center gap-3 p-1.5 pr-3 rounded-xl transition-colors border group ${viewAsUser ? 'bg-clip-50 border-clip-300 hover:bg-clip-100' : 'border-transparent hover:bg-gray-50 hover:border-gray-100'}`}
                        >
                            <div className="relative">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-clip-500 to-orange-400 flex items-center justify-center text-white text-sm font-bold shadow-md shadow-clip-200 group-hover:scale-105 transition-transform">
                                    {getInitials(fullName)}
                                </div>
                                {viewAsUser && (
                                    <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-clip-500 border-2 border-white flex items-center justify-center">
                                        <Eye size={10} className="text-white" />
                                    </span>
                                )}
                            </div>
                            <div className="text-left hidden md:block">
                                <p className="text-sm font-semibold text-gray-700 leading-none group-hover:text-clip-600 transition-colors">
                                    {viewAsUser && <span className="text-clip-600 font-bold">{t('topbar.view_as_prefix', 'Vista:')} </span>}
                                    {fullName}
                                </p>
                                <p className={`text-[10px] font-medium mt-1 truncate max-w-[160px] ${viewAsUser ? 'text-clip-600' : 'text-gray-400'}`}>
                                    {roleLabel}{organization?.name ? ` · ${organization.name}` : ''}
                                </p>
                            </div>
                            <ChevronDown size={14} className="text-gray-400 hidden md:block" />
                        </button>

                        {isProfileOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => setIsProfileOpen(false)}></div>
                                <div className="absolute left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 z-50 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="px-4 py-3 border-b border-gray-50 mb-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-sm font-semibold text-gray-900 truncate">{fullName}</p>
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${permissionLevel === 'owner' ? 'bg-clip-50 text-clip-700 border border-clip-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                                                {roleLabel}
                                            </span>
                                        </div>
                                        {displayEmail && <p className="text-xs text-gray-500 truncate mt-0.5">{displayEmail}</p>}
                                        {organization?.name && <p className="text-xs text-gray-400 truncate mt-0.5">{organization.name}</p>}
                                        <p className="text-[10px] text-gray-400 mt-2">
                                            {viewAsUser
                                                ? t('topbar.view_as_note', 'Stai vedendo Clip come questo dipendente.')
                                                : t('topbar.account_note', 'Le azioni vengono attribuite a questo account.')}
                                        </p>
                                    </div>

                                    {/* Vista attiva: torna all'account reale */}
                                    {viewAsUser && (
                                        <button
                                            onClick={() => { stopViewAs(); setIsProfileOpen(false); }}
                                            className="w-full text-left px-4 py-3 text-sm font-bold text-clip-700 bg-clip-50/60 hover:bg-clip-100 flex items-center gap-3 transition-colors"
                                        >
                                            <Undo2 size={18} /> {t('topbar.back_to_account', 'Torna al tuo account ({{name}})', { name: realFirstName })}
                                        </button>
                                    )}

                                    <button onClick={() => { onNavigate(Page.PROFILE); setIsProfileOpen(false); }} className="w-full text-left px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-clip-600 flex items-center gap-3 transition-colors">
                                        <UserIcon size={18} /> {t('sidebar.PROFILE')}
                                    </button>
                                    <button onClick={() => { onNavigate(Page.SETTINGS); setIsProfileOpen(false); }} className="w-full text-left px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-clip-600 flex items-center gap-3 transition-colors">
                                        <SettingsIcon size={18} /> {t('sidebar.SETTINGS')}
                                    </button>

                                    {/* "Vedi come": solo per il titolare, fuori dalla vista */}
                                    {isRealAdmin && !viewAsUser && viewAsCandidates.length > 0 && (
                                        <div className="border-t border-gray-50 mt-2 pt-2">
                                            <p className="px-4 pt-1 pb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                                                <Eye size={12} /> {t('topbar.view_as_title', 'Vedi come')}
                                            </p>
                                            {viewAsCandidates.map((u: any) => {
                                                const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
                                                const uRole = u.role === 'admin'
                                                    ? t('topbar.role.owner', 'Titolare')
                                                    : (u.permission_level === 'senior' ? t('topbar.role.senior', 'Senior') : t('topbar.role.junior', 'Junior'));
                                                return (
                                                    <button
                                                        key={u.id}
                                                        onClick={() => { startViewAs(u); setIsProfileOpen(false); }}
                                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-clip-600 flex items-center gap-3 transition-colors"
                                                    >
                                                        <span className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0">
                                                            {getInitials(name)}
                                                        </span>
                                                        <span className="flex-1 truncate">{name}</span>
                                                        <span className="text-[10px] text-gray-400 flex-shrink-0">{uRole}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* LOGOUT NEL MENU PROFILO (standard SaaS, non più prominente in alto) */}
                                    <div className="border-t border-gray-50 mt-2 pt-2">
                                        <button onClick={handleLogout} className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors">
                                            <LogOut size={18} /> {t('common.logout')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* CENTRO — centrato in assoluto rispetto all'header (non spinto dai lati) */}
                <div className="hidden md:flex items-center gap-2 px-5 py-2 bg-gray-50 rounded-full border border-gray-100 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <Hand size={16} className="text-clip-500" />
                    <span className="text-sm font-medium text-gray-600">
                        {t('home.greeting', { name: firstName || 'Utente' })}
                    </span>
                </div>

                {/* DESTRA */}
                <div className="flex items-center gap-3 relative">
                    <div className="relative">
                        <button
                            onClick={() => { setIsNotificationsOpen(!isNotificationsOpen); setIsProfileOpen(false); }}
                            className={`relative p-2.5 rounded-xl transition-all hover:scale-105 ${isNotificationsOpen ? 'bg-clip-50 text-clip-500' : 'text-gray-400 hover:text-clip-500 hover:bg-clip-50'}`}
                        >
                            <Bell size={20} />
                            {unreadCount > 0 && (
                                <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                            )}
                        </button>

                        {isNotificationsOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setIsNotificationsOpen(false)}></div>
                                <div className="absolute right-0 mt-4 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="px-5 py-4 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                                        <h3 className="font-bold text-gray-900">{t('notifications.title')} ({unreadCount})</h3>
                                        <button onClick={() => markAllAsRead()} className="text-xs font-bold text-clip-600 hover:text-clip-800">
                                            Segna tutte come lette
                                        </button>
                                    </div>

                                    <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                                        {notifications.length === 0 ? (
                                            <div className="p-8 text-center text-gray-400 text-sm">Nessuna notifica</div>
                                        ) : (
                                            notifications.slice(0, 5).map((notif: any) => (
                                                <div key={notif.id} onClick={() => markAsRead(notif.id)} className={`p-4 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer flex gap-4 ${!notif.read ? 'bg-orange-50/30' : ''}`}>
                                                    <div className={`mt-1 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gray-100`}>
                                                        {getNotifIcon(notif.type)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className={`text-sm ${!notif.read ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>{notif.title}</p>
                                                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{notif.message}</p>
                                                        <p className="text-[10px] text-gray-400 mt-2 font-medium">{formatDate(notif.created_at)}</p>
                                                    </div>
                                                    {!notif.read && <div className="w-2 h-2 bg-clip-500 rounded-full mt-2 flex-shrink-0"></div>}
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <div className="p-3 bg-gray-50 text-center border-t border-gray-100">
                                        <button
                                            onClick={() => {
                                                setIsNotificationsOpen(false);
                                                setIsFullNotificationsModalOpen(true);
                                            }}
                                            className="text-xs font-bold text-gray-500 hover:text-gray-900 transition-colors w-full h-full block"
                                        >
                                            {t('notifications.view_all')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* MODALE NOTIFICHE */}
            <NotificationsModal
                isOpen={isFullNotificationsModalOpen}
                onClose={() => setIsFullNotificationsModalOpen(false)}
            />
        </>
    );
};

export default TopBar;